/**
 * Deen Over Dunya — Shared Constants & Utilities
 */

/* ============================================
   Era labels & ordering
   ============================================ */

export const ERA_LABELS: Record<string, string> = {
  pre_prophethood: "Pre-Prophethood",
  meccan_early: "Early Meccan",
  meccan_middle: "Middle Meccan",
  meccan_late: "Late Meccan",
  medinan_early: "Early Medinan",
  medinan_middle: "Middle Medinan",
  medinan_late: "Late Medinan",
  post_conquest: "Post-Conquest",
};

export const ERA_ORDER = Object.keys(ERA_LABELS);

/* ============================================
   Civilization labels
   ============================================ */

export const CIVILIZATION_LABELS: Record<string, string> = {
  byzantine: "Byzantine Empire",
  sassanid: "Sassanid Empire",
  tang_china: "Tang Dynasty (China)",
  sui_china: "Sui Dynasty (China)",
  axumite: "Aksumite Empire",
  visigothic: "Visigothic Kingdom",
  frankish: "Frankish Kingdom",
  indian: "Indian Kingdoms",
  turkic: "Turkic Khaganate",
  lakhmid: "Lakhmid Kingdom",
  ghassanid: "Ghassanid Kingdom",
  himyarite: "Himyarite Kingdom",
  multiple: "Multiple Civilizations",
};

/* ============================================
   Category labels
   ============================================ */

export const CATEGORY_LABELS: Record<string, string> = {
  revelation: "Revelation",
  battle: "Battle",
  treaty: "Treaty",
  migration: "Migration",
  social: "Social",
  legislation: "Legislation",
  miracle: "Miracle",
  personal: "Personal",
  death: "Death",
  military: "Military",
  political: "Political",
  religious: "Religious",
  natural: "Natural Event",
  cultural: "Cultural",
};

/* ============================================
   Source type labels & icons
   ============================================ */

export const SOURCE_ICONS: Record<string, { emoji: string; label: string }> = {
  quran: { emoji: "📖", label: "Quran" },
  hadith_sahih: { emoji: "📜", label: "Sahih Hadith" },
  hadith_hasan: { emoji: "📜", label: "Hasan Hadith" },
  scholarly: { emoji: "🎓", label: "Scholarly" },
  academic_book: { emoji: "📚", label: "Academic Book" },
  academic_article: { emoji: "📄", label: "Academic Article" },
};

/* ============================================
   Formatting helpers
   ============================================ */

export function formatHijriYear(yearCE: number): string {
  const hijri = yearCE - 622;
  if (hijri <= 0) return `${Math.abs(hijri)} BH`;
  return `${hijri} AH`;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).replace(/\s+\S*$/, "") + "…";
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? `${count} ${singular}` : `${count} ${plural || singular + "s"}`;
}

/* ============================================
   Local storage helpers (for bookmarks, prefs)
   ============================================ */

export function getStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`dod:${key}`);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function setStored<T>(key: string, value: T): void {
  try {
    localStorage.setItem(`dod:${key}`, JSON.stringify(value));
  } catch {
    // silently fail
  }
}
